import React, { useEffect, useMemo, useState } from 'react';
import { Input, Pagination, Divider, Spin, Modal } from 'antd';
import { WithLatestStateDeviceNode } from '../RootTree';
import { LoadingOutlined } from '@ant-design/icons';
import {
  useGetDevicesByGroupId,
  useListDeviceByDeviceGroupId,
  useRemoveCamera,
} from '../../../../hooks/socket/cameras';
import { useDispatch, useSelector } from 'react-redux';
import { updateRecoveryCameraPage } from '../../../../redux/recovery/action';
import { getRecoveryCameraPage } from '../../../../redux/recovery/reducer';
import { beeXLogger } from '../../../../helpers/logger';

const GroupCamerasV1 = ({
  cameras,
  currentPage,
  pageSize,
  onPageChange,
  isLoading,
  total,
  getIsCameraPinned,
  onPinCamera,
  selectedSerial,
  onSelectCamera,
  onUpdateCamera,
  onUpdateStream,
  onRemoveCamera,
  allowEdit,
  allowRemove,
}) => {
  const searchPageSize = 15;
  const [search, setSearch] = useState('');
  const [searchPage, setSearchPage] = useState(1);
  const searchResult = useMemo(() => {
    if (!search) {
      return cameras;
    }

    return cameras?.filter((cam) =>
      cam.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [cameras, search]);
  const dispatch = useDispatch();
  const { searchCameraKeyword: recoverySearchCameraKeyword } = useSelector(
    getRecoveryCameraPage,
  );

  const handleSearch = (value) => {
    setSearch(value);
    setSearchPage(1);
    dispatch(
      updateRecoveryCameraPage({
        searchCameraKeyword: value,
      }),
    );
  };

  useEffect(() => {
    if (recoverySearchCameraKeyword) {
      setSearch(recoverySearchCameraKeyword);
      setSearchPage(1);
    }
  }, [recoverySearchCameraKeyword]);

  const camerasToDisplay = useMemo(() => {
    if (!search) {
      return cameras?.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      );
    }

    return searchResult?.slice(
      (searchPage - 1) * searchPageSize,
      searchPage * searchPageSize,
    );
  }, [
    search,
    searchResult,
    currentPage,
    pageSize,
    searchPage,
    searchPageSize,
    cameras,
  ]);

  const finalCurrentPage = useMemo(() => {
    if (!search) {
      return currentPage;
    }

    return searchPage;
  }, [search, searchPage, currentPage]);

  const finalTotal = useMemo(() => {
    if (!search) {
      return total;
    }

    return searchResult?.length || 0;
  }, [search, searchResult, total]);

  const handlePageChange = (page, pageSize) => {
    if (!search) {
      onPageChange(page, pageSize);
    } else {
      setSearchPage(page);
    }
  };

  const finalPageSize = useMemo(() => {
    if (!search) {
      return pageSize;
    }

    return searchPageSize;
  }, [search, pageSize, searchPageSize]);
  return (
    <>
      <Input.Search
        placeholder="Search this group"
        onSearch={handleSearch}
        value={search}
        loading={isLoading}
        allowClear
        onChange={(e) => setSearch(e.target.value)}
      />
      <Divider className="camera-list-divider" />
      <div className="camera-list">
        {isLoading && (
          <div className="camera-list-loading">
            <Spin indicator={<LoadingOutlined />} />
          </div>
        )}
        {!isLoading && (
          <React.Fragment>
            <div className="camera-list-items">
              {camerasToDisplay?.map((cam) => (
                <WithLatestStateDeviceNode
                  key={cam.serial}
                  name={cam.name}
                  serial={cam.serial}
                  state={cam.state}
                  pinned={getIsCameraPinned?.(cam.serial) || false}
                  onPin={(pinned) => onPinCamera(cam, pinned)}
                  isSelected={selectedSerial === cam.serial}
                  onSelect={() => onSelectCamera(cam)}
                  allowRemove={allowRemove}
                  camera={allowEdit ? cam : undefined}
                  variant="group"
                  onUpdate={onUpdateCamera}
                  onUpdateStream={onUpdateStream}
                  onRemove={() => onRemoveCamera(cam.serial)}
                />
              ))}
            </div>

            <div className="camera-list-pagination">
              <Pagination
                size="small"
                showSizeChanger={false}
                total={finalTotal}
                current={finalCurrentPage}
                pageSize={finalPageSize}
                onChange={handlePageChange}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    </>
  );
};

const paginate = (cameras, currentPage, pageSize) => {
  return cameras?.slice((currentPage - 1) * pageSize, currentPage * pageSize);
};

export const GroupCameras = ({
  getIsCameraPinned,
  onPinCamera,
  selectedSerial,
  onSelectCamera,
  groupId,
  namespace,
  cameras: propsCameras,
  shouldFetch,
  onUpdateCamera,
  onUpdateStream,
  updateReturn,
  onCameraRemoved,
}) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 1000,
  });
  const { data, isLoading, send, patch } = useListDeviceByDeviceGroupId();
  const {
    data: groupData,
    send: getGroupData,
    isLoading: isGroupLoading,
  } = useGetDevicesByGroupId();

  useEffect(() => {
    if (shouldFetch) {
      beeXLogger.debug("🚀 ~ GroupCameras => fetch group => body:", {
        namespace,
        deviceGroupId: groupId,
        timeRequest: Date.now(),
      })
      send({
        namespace,
        deviceGroupId: groupId,
        timeRequest: Date.now(),
      });
    }
  }, [send, namespace, groupId, shouldFetch]);

  useEffect(() => {
    if (groupId > 0) {
      getGroupData({
        namespace,
        deviceGroupId: groupId,
        timeRequest: Date.now(),
      });
    }
  }, [getGroupData, namespace, groupId]);

  useEffect(() => {
    if (updateReturn && updateReturn.isSuccess) {
      console.log('updateReturn', updateReturn);
      patch((prev) => {
        if (!prev) {
          return prev;
        }

        if (
          !prev.cameraInfos.find((d) => d.serial === updateReturn.data.serial)
        ) {
          return prev;
        }

        return {
          ...prev,
          cameraInfos: prev.cameraInfos.map((d) =>
            d.serial === updateReturn.data.serial
              ? {
                ...d,
                ...updateReturn.data,
              }
              : d,
          ),
        };
      });
    }
  }, [updateReturn, patch]);

  const { removeCamera } = useRemoveCamera();

  const doRemoveCamera = (serial) => {
    if (groupId > 0) {
      return removeCamera(serial, groupId, namespace)
        .then(() => {
          if (onCameraRemoved) {
            onCameraRemoved(groupId, serial);
          }
        })
        .then(() => {
          patch((prev) => {
            if (!prev) {
              return prev;
            }

            return {
              ...prev,
              cameraInfos: prev.cameraInfos.filter((d) => d.serial !== serial),
            };
          });
        });
    } else {
      if (onCameraRemoved) {
        onCameraRemoved(groupId, serial);
      }
    }
  };

  const onRemoveCamera = (serial) => {
    Modal.confirm({
      title: 'Remove camera',
      content: 'Are you sure you want to remove this camera?',
      onOk: () => doRemoveCamera(serial),
    });
  };

  const allCameras = useMemo(() => {
    if (shouldFetch) {
      beeXLogger.debug("🚀 ~ GroupCameras => allCameras:", data?.cameraInfos)
      return data?.cameraInfos || [];
    }

    return propsCameras || [];
  }, [shouldFetch, data, propsCameras]);

  const total = allCameras.length;
  const canModify = groupId > 0 && groupData?.deviceGroup?.permissionGroup;


  return (
    <GroupCamerasV1
      cameras={allCameras}
      currentPage={pagination.current}
      pageSize={pagination.pageSize}
      onPageChange={(page, pageSize) =>
        setPagination({ ...pagination, current: page, pageSize })
      }
      isLoading={isLoading}
      total={total}
      getIsCameraPinned={getIsCameraPinned}
      onPinCamera={onPinCamera}
      selectedSerial={selectedSerial}
      onSelectCamera={onSelectCamera}
      allowEdit={canModify}
      allowRemove={canModify}
      onRemoveCamera={onRemoveCamera}
      onUpdateCamera={onUpdateCamera}
      onUpdateStream={onUpdateStream}
    />
  );
};
